export type Countdown = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
};

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * 60;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

/**
 * Time remaining from `nowMs` to `endsAtMs`, split into d/h/m/s.
 * A past (or equal) end time yields all-zero with `expired: true`, so callers
 * hide the promo/countdown rather than showing a negative or reset timer.
 */
export const computeCountdown = (
  endsAtMs: number,
  nowMs: number,
): Countdown => {
  const remaining = endsAtMs - nowMs;
  if (remaining <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  const totalSeconds = Math.floor(remaining / MS_PER_SECOND);
  return {
    days: Math.floor(totalSeconds / SECONDS_PER_DAY),
    hours: Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR),
    minutes: Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE),
    seconds: totalSeconds % SECONDS_PER_MINUTE,
    expired: false,
  };
};

/** Parse the backend ISO `ends_at` to epoch ms, or null when absent/invalid. */
export const parseEndsAt = (
  endsAt: string | null | undefined,
): number | null => {
  if (!endsAt) return null;
  const ms = Date.parse(endsAt);
  return Number.isNaN(ms) ? null : ms;
};
